/**
 * Feb 2026 — Session stability fix (AuthProvider).
 *
 * ─── Qué cambió ────────────────────────────────────────────────────────────
 *   • refresh() ya no desloguea ante cualquier error. Diferencia:
 *       - 401 real (detail en INVALID_TOKEN_DETAILS)        → logout limpio
 *       - cualquier otro error (red, timeout, 5xx, CORS)   → mantiene token
 *                                                             + user en cache
 *                                                             + reintenta
 *   • Reintentos exponenciales 500ms / 1500ms / 4000ms antes de rendirse.
 *   • Al rendirse por errores transitorios NO borra el token: el usuario
 *     sigue autenticado y se puede reintentar al recuperar red.
 *   • Escucha el evento `hittcg:session-killed` del interceptor para hacer
 *     redirect limpio a /login cuando el token sí está muerto.
 */
import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

// Strings EXACTOS de 401 reales (mismo set que api.js, duplicado a propósito
// para que este módulo no dependa del orden de carga).
const INVALID_TOKEN_DETAILS = new Set([
  "No autenticado",
  "Token expirado",
  "Token inválido",
  "Usuario no encontrado",
  "Sesión cerrada por cambio de contraseña",
]);

// Backoff solicitado explícitamente por el usuario: 500ms, 1500ms, 4000ms.
const RETRY_DELAYS_MS = [500, 1500, 4000];
const RECONNECT_EVERY_MS = 5000;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function isInvalidTokenError(err) {
  if (err?.response?.status !== 401) return false;
  const detail = err.response?.data?.detail;
  return typeof detail === "string" && INVALID_TOKEN_DETAILS.has(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconnecting, setReconnecting] = useState(false);
  const retryTimer = useRef(null);

  const refresh = useCallback(async () => {
    clearTimeout(retryTimer.current);
    const token = localStorage.getItem("hittcg_token");
    if (!token) {
      setUser(null);
      setReconnecting(false);
      setLoading(false);
      return;
    }

    // Hasta 1 intento inicial + 3 retries = 4 llamadas totales.
    // Pedimos __noRetry al interceptor para manejar nosotros los backoffs
    // (si no, api.js y auth.jsx se sumarían y daría retries dobles).
    for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
      try {
        const r = await api.get("/auth/me", { __noRetry: true });
        setUser(r.data);
        setReconnecting(false);
        setLoading(false);
        return;
      } catch (err) {
        // Token REALMENTE muerto → logout limpio.
        if (isInvalidTokenError(err)) {
          localStorage.removeItem("hittcg_token");
          const d = err?.response?.data?.detail;
          if (typeof d === "string") sessionStorage.setItem("hittcg_logout_reason", d);
          setUser(null);
          setReconnecting(false);
          setLoading(false);
          return;
        }
        // Transitorio (red / timeout / 5xx / 401 ambiguo): reintentar.
        if (attempt < RETRY_DELAYS_MS.length) {
          await sleep(RETRY_DELAYS_MS[attempt]);
          continue;
        }
        // Agotamos retries con token guardado (p.ej. servidor reiniciándose en un deploy):
        // NO borramos token ni mandamos a /login. "Reconectando…" y seguimos intentando.
        setReconnecting(true);
        retryTimer.current = setTimeout(() => refresh(), RECONNECT_EVERY_MS);
        return;
      }
    }
  }, []);

  useEffect(() => { refresh(); return () => clearTimeout(retryTimer.current); }, [refresh]);

  // Escucha el evento emitido por api.js cuando se confirma token muerto.
  useEffect(() => {
    const handler = () => {
      clearTimeout(retryTimer.current);
      setReconnecting(false);
      setUser(null);
      const path = window.location.pathname;
      if (!path.startsWith("/login") && !path.startsWith("/register")) {
        window.location.href = "/login";
      }
    };
    window.addEventListener("hittcg:session-killed", handler);
    return () => window.removeEventListener("hittcg:session-killed", handler);
  }, []);

  const login = async (email, password, totpCode) => {
    const r = await api.post("/auth/login", { email, password, totp_code: totpCode });
    if (r.data.require_totp) return { requireTotp: true };
    localStorage.setItem("hittcg_token", r.data.token);
    await refresh();
    return { role: r.data.role };
  };

  const register = async (payload) => {
    const r = await api.post("/auth/register", payload);
    if (r.data?.pending) return r.data;
    localStorage.setItem("hittcg_token", r.data.token);
    await refresh();
    return r.data;
  };

  const loginWithToken = async (token) => {
    localStorage.setItem("hittcg_token", token);
    await refresh();
  };

  const logout = () => {
    localStorage.removeItem("hittcg_token");
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthCtx.Provider value={{ user, loading, reconnecting, login, register, loginWithToken, logout, refresh }}>
      {reconnecting && !/^\/(login|register)/.test(window.location.pathname) && (
        <div role="status" data-testid="auth-reconnecting"
             className="fixed top-0 inset-x-0 z-[9999] bg-amber-500 text-black text-center text-sm font-semibold py-2 px-4">
          Estamos actualizando HitTCG… reconectando, vuelve en un momento.
        </div>
      )}
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth outside provider");
  return ctx;
}
